import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';

interface PopulationChartProps {
  data: Record<string, number>;
}

const AGE_GROUPS = [
  { key: 'population_0_3_y', label: '0–3' },
  { key: 'population_4_6_y', label: '4–6' },
  { key: 'population_7_12_y', label: '7–12' },
  { key: 'population_13_15_y', label: '13–15' },
  { key: 'population_16_19_y', label: '16–19' },
  { key: 'population_20_24_y', label: '20–24' },
  { key: 'population_25_34_y', label: '25–34' },
  { key: 'population_35_44_y', label: '35–44' },
  { key: 'population_45_60_y', label: '45–60' },
  { key: 'population_61_64_y', label: '61–64' },
  { key: 'population_65_75_y', label: '65–75' },
  { key: 'population_76_plus_y', label: '76+' },
];

const formatNumber = (value: number) => value.toLocaleString('sv-SE');

export function PopulationChart({ data }: PopulationChartProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const chartData = AGE_GROUPS.map((g) => ({
    age: g.label,
    antal: data[g.key] ?? 0,
  }));

  const tickSize = isMobile ? 9 : 11;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm md:text-base">{t('postal.charts.populationByAge')}</CardTitle>
      </CardHeader>
      <CardContent className="px-2 md:px-6">
        <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
          <BarChart data={chartData} margin={{ bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="age"
              tick={{ fontSize: tickSize }}
              interval={0}
              angle={-35}
              textAnchor="end"
              height={50}
            />
            <YAxis tick={{ fontSize: tickSize }} tickFormatter={formatNumber} width={isMobile ? 35 : 60} />
            <Tooltip
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value: number) => [formatNumber(value), t('postal.charts.persons')]}
              labelFormatter={(label) => `${t('postal.charts.ageGroup')}: ${label}`}
            />
            <Bar dataKey="antal" fill="hsl(195, 85%, 42%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
